import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface NotificationPreference {
  kind: NotificationKind;
  label: string;
  enabled: boolean;
  module: NotificationModule;
  moduleLabel: string;
  moduleIcon: string;
}

export type NotificationKind =
  | 'delivery_store_order_created' | 'delivery_store_order_partial' | 'delivery_store_order_completed'
  | 'delivery_order_created' | 'delivery_order_partial' | 'delivery_order_completed'
  | 'chat'
  | 'task'
  | 'meeting'
  | 'destino_bol'
  | 'sales-gmail'
  | 'item_reviewed'
  | 'new_items'
  | 'payment_method_priority'
  | 'payment_confirmed' | 'payment_pending' | 'payment_cancelled'
  | 'special_order_cancelled_by_sales'
  | 'special_order_cancelled_by_supplier'
  | 'special_order_estimated_date'
  | 'unconsidered_item_sku'
  | 'inventory_no_saleable_stock'
  | 'invoice_swap'
  | 'invoice_cancellation'
  | 'inventory_new_item'
  | 'special_order_stock_assignment'
  | 'inventory_location_movement'
  | 'inventory_discontinued'
  | 'display_bed_mattress_orphaned';

export type NotificationModule = 'collaboration' | 'purchases' | 'inventory' | 'sales' | 'administracion' | 'logistics';

export interface NotificationPreferenceGroup {
  module: NotificationModule;
  label: string;
  icon: string;
  preferences: NotificationPreference[];
}

export const NOTIFICATION_KINDS: NotificationKind[] = [
  'delivery_store_order_created', 'delivery_store_order_partial', 'delivery_store_order_completed',
  'delivery_order_created', 'delivery_order_partial', 'delivery_order_completed',
  'chat',
  'task',
  'meeting',
  'destino_bol',
  'sales-gmail',
  'item_reviewed',
  'new_items',
  'payment_method_priority',
  'payment_confirmed',
  'payment_cancelled',
  'payment_pending',
  'special_order_cancelled_by_sales',
  'special_order_cancelled_by_supplier',
  'special_order_estimated_date',
  'unconsidered_item_sku',
  'inventory_no_saleable_stock',
  'invoice_swap',
  'invoice_cancellation',
  'inventory_new_item',
  'special_order_stock_assignment',
  'inventory_location_movement',
  'inventory_discontinued',
  'display_bed_mattress_orphaned'
];

interface NotificationModuleDefinition {
  module: NotificationModule;
  label: string;
  icon: string;
}

export const NOTIFICATION_MODULES: NotificationModuleDefinition[] = [
  { module: 'logistics', label: 'Logística', icon: 'pi pi-truck' },
  { module: 'collaboration', label: 'Colaboración', icon: 'pi pi-comments' },
  { module: 'purchases', label: 'Compras', icon: 'pi pi-shopping-cart' },
  { module: 'inventory', label: 'Inventario y Visual', icon: 'pi pi-box' },
  { module: 'sales', label: 'Ventas', icon: 'pi pi-chart-line' },
  { module: 'administracion', label: 'Administración', icon: 'pi pi-wallet' }
];

const KIND_METADATA: Record<NotificationKind, {
  label: string;
  module: NotificationModule;
}> = {
  delivery_order_created: { label: 'Entregas a domicilio solicitadas', module: 'logistics' },
  delivery_order_partial: { label: 'Entregas a domicilio parciales', module: 'logistics' },
  delivery_order_completed: { label: 'Entregas a domicilio completadas', module: 'logistics' },
  delivery_store_order_created: { label: 'Entregas en tienda solicitadas', module: 'inventory' },
  delivery_store_order_partial: { label: 'Entregas en tienda parciales', module: 'inventory' },
  delivery_store_order_completed: { label: 'Entregas en tienda completadas', module: 'inventory' },
  chat: { label: 'Mensajes de chat', module: 'collaboration' },
  task: { label: 'Tareas', module: 'collaboration' },
  meeting: { label: 'Reuniones', module: 'collaboration' },
  destino_bol: { label: 'Cambios de destino BOL', module: 'inventory' },
  'sales-gmail': { label: 'Correos de ventas', module: 'sales' },
  item_reviewed: { label: 'Items revisados', module: 'purchases' },
  payment_method_priority: { label: 'Prioridad bancaria', module: 'sales' },
  new_items: { label: 'Nuevos artículos', module: 'sales' },
  payment_pending: { label: 'Pagos pendientes de confirmación', module: 'administracion' },
  payment_confirmed: { label: 'Pagos confirmados', module: 'administracion' },
  payment_cancelled: { label: 'Cancelación de pago', module: 'administracion' },
  special_order_cancelled_by_sales: { label: 'Pedidos especiales cancelados por Ventas', module: 'purchases' },
  special_order_cancelled_by_supplier: { label: 'Pedidos especiales cancelados por proveedor', module: 'sales' },
  special_order_estimated_date: { label: 'Cambios de fecha estimada', module: 'purchases' },
  unconsidered_item_sku: { label: 'Items no considerados en recepción', module: 'purchases' },
  inventory_no_saleable_stock: { label: 'Sin unidades vendibles (No vender)', module: 'inventory' },
  invoice_swap: { label: 'Swaps en invoices', module: 'sales' },
  invoice_cancellation: { label: 'Cancelaciones de invoices e items', module: 'sales' },
  inventory_new_item: { label: 'Artículos nuevos en inventario', module: 'inventory' },
  special_order_stock_assignment: { label: 'Asignación de PE a In Stock', module: 'sales' },
  inventory_location_movement: { label: 'Movimientos de ubicaciones', module: 'inventory' },
  inventory_discontinued: { label: 'Artículos descontinuados', module: 'inventory' },
  display_bed_mattress_orphaned: { label: 'Camas o colchones sin pareja en exhibición', module: 'inventory' }
};

@Injectable({ providedIn: 'root' })
export class NotificationPreferencesService {
  private endpoint = `${environment.endpoint}collaboration/notifications`;

  constructor(private http: HttpClient) {}

  getPreferences(): Observable<NotificationPreference[]> {
    return this.http.get<unknown>(`${this.endpoint}/preferences`).pipe(
      map(response => {
        const rawItems = Array.isArray(response)
          ? response
          : this.getWrappedItems(response);
        const enabledByKind = new Map<NotificationKind, boolean>();

        for (const rawItem of rawItems) {
          if (!rawItem || typeof rawItem !== 'object') {
            continue;
          }

          const item = rawItem as { kind?: unknown; enabled?: unknown };
          const kind = this.normalizeKind(item.kind);
          if (kind && typeof item.enabled === 'boolean') {
            enabledByKind.set(kind, item.enabled);
          }
        }

        return NOTIFICATION_KINDS.map(kind => NotificationPreferencesService.createPreference(kind, enabledByKind.get(kind) ?? true));
      })
    );
  }

  static createDefaultPreferences(): NotificationPreference[] {
    return NOTIFICATION_KINDS.map(kind => this.createPreference(kind, true));
  }

  static groupPreferences(preferences: NotificationPreference[]): NotificationPreferenceGroup[] {
    return NOTIFICATION_MODULES
      .map(module => ({
        module: module.module,
        label: module.label,
        icon: module.icon,
        preferences: preferences.filter(preference => preference.module === module.module)
      }))
      .filter(group => group.preferences.length > 0);
  }

  private static createPreference(kind: NotificationKind, enabled: boolean): NotificationPreference {
    const metadata = KIND_METADATA[kind];
    const module = NOTIFICATION_MODULES.find(item => item.module === metadata.module)!;

    return {
      kind,
      label: metadata.label,
      enabled,
      module: metadata.module,
      moduleLabel: module.label,
      moduleIcon: module.icon
    };
  }

  private getWrappedItems(response: unknown): unknown[] {
    if (!response || typeof response !== 'object') {
      return [];
    }

    const wrapped = response as {
      preferences?: unknown;
      items?: unknown;
      data?: unknown;
    };

    if (Array.isArray(wrapped.preferences)) return wrapped.preferences;
    if (Array.isArray(wrapped.items)) return wrapped.items;
    if (Array.isArray(wrapped.data)) return wrapped.data;
    return [];
  }

  private normalizeKind(value: unknown): NotificationKind | null {
    const kind = String(value ?? '').replaceAll('\\', '').trim();
    return NOTIFICATION_KINDS.includes(kind as NotificationKind)
      ? kind as NotificationKind
      : null;
  }

  updatePreference(kind: string, enabled: boolean): Observable<any> {
    return this.http.put(`${this.endpoint}/preferences`, { kind, enabled });
  }
}
